// Photo-Sphere-Viewer host: owns the lifecycle of the panorama overlay
// inside .vmc-video-clip. Pure DOM + PSV glue; no Leaflet, no telemetry.
//
// createPanoHost(clipEl, opts) returns { hostEl, open, close }.
//
// opts.psvCtorLoader  — DI seam. Defaults to dynamic import of
//                       @photo-sphere-viewer/core's `Viewer` class.
// opts.documentImpl   — DI seam for tests. Defaults to globalThis.document.

import { computeSupersampleSize } from "./viewer.freeze.geometry.js";

const DEFAULT_LOADER = () =>
  import("@photo-sphere-viewer/core").then((m) => m.Viewer);

export function createPanoHost(clipEl, opts = {}) {
  const loader = opts.psvCtorLoader ?? DEFAULT_LOADER;
  const doc = opts.documentImpl ?? globalThis.document;

  const hostEl = doc.createElement("div");
  hostEl.className = "vmc-pano-host";
  hostEl.hidden = true;
  clipEl.appendChild(hostEl);

  const closeBtn = doc.createElement("button");
  closeBtn.className = "vmc-pano-close";
  closeBtn.type = "button";
  closeBtn.textContent = "\u2715 Close panorama";
  closeBtn.hidden = true;
  clipEl.appendChild(closeBtn);

  let activeInstance = null;
  let activeOnClose = null;
  let activePanorama = null;

  function close() {
    if (activeInstance && typeof activeInstance.destroy === "function") {
      try { activeInstance.destroy(); } catch { /* swallow teardown errors */ }
    }
    activeInstance = null;
    activePanorama = null;
    hostEl.hidden = true;
    closeBtn.hidden = true;
    clipEl.classList.remove("vmc-pano-mode");
    const cb = activeOnClose;
    activeOnClose = null;
    if (typeof cb === "function") cb();
  }

  closeBtn.addEventListener("click", close);

  async function open(panorama, options = {}) {
    const { video, onClose, initialView, caption } = options;
    if (activeInstance) close();
    activeOnClose = onClose ?? null;
    activePanorama = panorama;

    if (video && typeof video.pause === "function") video.pause();
    hostEl.hidden = false;
    closeBtn.hidden = false;

    const PsvCtor = await loader();
    if (PsvCtor === null) return; // test seam: loader may stub out the ctor
    const onFail = (label, err) => {
      console.error(`[pano] ${label}:`, err);
      // Revert UI so the video remains visible/scrubbable.
      hostEl.hidden = true;
      closeBtn.hidden = true;
      clipEl.classList.remove("vmc-pano-mode");
      activeOnClose = null;
    };
    try {
      activeInstance = new PsvCtor({
        container: hostEl,
        panorama: panorama.url,
        navbar: caption ? ["caption", "zoom", "fullscreen"] : ["zoom", "fullscreen"],
        caption: caption ?? undefined,
        defaultYaw: initialView?.yaw ?? 0,
        defaultPitch: initialView?.pitch ?? 0,
        defaultZoomLvl: initialView?.zoom ?? 0,
        mousewheel: true,
        touchmoveTwoFingers: false,
      });
      activeInstance.addEventListener?.("panorama-error", (e) => {
        onFail("panorama-error", e?.error ?? e);
      });
      activeInstance.addEventListener?.("panorama-loaded", () => {
        console.info("[pano] panorama-loaded", panorama.id ?? panorama.url);
      });
      activeInstance.addEventListener?.("ready", () => {
        console.info("[pano] ready", panorama.id ?? panorama.url);
        // Only hide the video once PSV is actually showing something.
        clipEl.classList.add("vmc-pano-mode");
      });
    } catch (err) {
      onFail("PSV ctor threw", err);
    }
  }

  // True while a PSV instance is mounted. Note: open() sets activePanorama
  // before the async PSV constructor resolves, so isActive() only flips true
  // once construction completes — always `await open(...)` before relying on it.
  function isActive() {
    return activeInstance !== null;
  }

  // Render the panorama at supersampled resolution and return a 2D canvas
  // plus the active panorama and PSV view state. Returns null if no
  // panorama is open or no pixels could be produced.
  async function captureView() {
    const psv = activeInstance;
    const pano = activePanorama;
    if (psv === null || pano === null) return null;

    const pos = typeof psv.getPosition === "function"
      ? psv.getPosition()
      : { yaw: 0, pitch: 0 };
    const zoomLvl = typeof psv.getZoomLevel === "function"
      ? psv.getZoomLevel()
      : 0;
    const view = {
      yaw: Number.isFinite(pos?.yaw) ? pos.yaw : 0,
      pitch: Number.isFinite(pos?.pitch) ? pos.pitch : 0,
      zoom: Number.isFinite(zoomLvl) ? zoomLvl : 0,
    };

    let canvas = null;
    try {
      canvas = renderSupersampled(psv);
    } catch (err) {
      console.error("[pano] supersample capture failed, falling back:", err);
      canvas = renderFallback(psv);
    }
    if (canvas === null) return null;
    return { canvas, panorama: pano, view };
  }

  // Best path: grow the WebGL drawing buffer, force one render, copy it to a
  // 2D canvas, then restore the buffer size. Throws if PSV internals are
  // not reachable so captureView() can fall back.
  function renderSupersampled(psv) {
    const wrap = psv.renderer;
    const r3 = wrap && wrap.renderer;
    const scene = wrap && wrap.scene;
    const camera = wrap && wrap.camera;
    const dom = r3 && r3.domElement;
    if (!r3 || !scene || !camera || !dom) {
      throw new Error("PSV renderer internals unavailable");
    }
    // dom.width/.height are the WebGL backing-store pixel dimensions (not
    // the CSS clientWidth/Height) — the correct base for supersampling.
    const w0 = dom.width;
    const h0 = dom.height;
    if (!(w0 > 0) || !(h0 > 0)) throw new Error("PSV canvas has no size");
    const { W, H } = computeSupersampleSize(w0, h0);
    const out = doc.createElement("canvas");
    out.width = W;
    out.height = H;
    // Render + copy must both happen while the buffer is grown; the finally
    // restores it even if render() throws (lost WebGL context, OOM), so the
    // on-screen view is never left stuck at the enlarged size.
    r3.setSize(W, H, false);
    try {
      r3.render(scene, camera);
      out.getContext("2d").drawImage(dom, 0, 0, W, H);
    } finally {
      r3.setSize(w0, h0, false);
      if (typeof psv.needsUpdate === "function") psv.needsUpdate();
    }
    return out;
  }

  // Degraded path: copy whatever the on-screen WebGL canvas currently shows.
  function renderFallback(psv) {
    let dom = null;
    const r3 = psv.renderer && psv.renderer.renderer;
    if (r3 && r3.domElement) {
      dom = r3.domElement;
    } else if (psv.container && typeof psv.container.querySelector === "function") {
      dom = psv.container.querySelector("canvas");
    }
    if (!dom || !(dom.width > 0) || !(dom.height > 0)) return null;
    if (typeof psv.needsUpdate === "function") psv.needsUpdate();
    const out = doc.createElement("canvas");
    out.width = dom.width;
    out.height = dom.height;
    out.getContext("2d").drawImage(dom, 0, 0);
    return out;
  }

  return { hostEl, open, close, isActive, captureView };
}
